import * as React from 'react'
import * as ActionCable from 'actioncable'
import styled, { css, ThemeProvider } from 'styled-components'
import { readableColor } from 'polished'
import WidgetContainer from '../components/WidgetContainer'
import Launcher from '../components/Launcher'
import { BizzeySetting } from '../../../packs/widget_shim'
import { Conversation, ConversationMessage, ConversationMessageAuthorType, ConversationState, PublicLocale, Sounds, WidgetClientEvent, WidgetClientEventType, WidgetMessengerEvent, WidgetMessengerEventType, WidgetSessionChannelEvent, WidgetSessionChannelEventType, WidgetSpaceType } from '../../types'
import { Style } from '../styles'
import debounce from 'debounce';
import RouteChangeListener from '../components/RouteChangeListener'
import {
  hideLauncher,
  setApplicationState,
  setMessengerFrame,
  showLauncher,
  open,
  close,
  navigate,
  addShowLauncherCallback,
  addHideLauncherCallback,
  addReadyCallback,
  addOpenCallback,
  addCloseCallback,
  toggleOpen,
  maximizeWindow,
  minimizeWindow,
  toggleWindowSize,
  setBizzeySetting,
  setWidget,
  setWidgetSession,
  setWidgetCounters,
  setHostWindowSize,
} from '../store/application/actions'
import { connect } from 'react-redux'
import { ApplicationState } from '../store/application/types'
import { AppState } from '../store'
import { CookieHelper, MessageHelper, TourRunner, TourBuilder } from '../helpers'
import DEFAULT_THEME from '../styles/defaulTheme'
import { createPortal } from 'react-dom'
import Messenger from './Messenger/MessengerLayout'
import { WidgetController, OutboundMessagesController } from '../controllers'
import BaseController from '../controllers/BaseController'
import { initializeTranslations, setLocale } from '../translations'
import PreviewModal from '../components/Modals/PreviewModal'
import Notification from './Notification/Notification'
import ActionCableProvider from '../../providers/ActionCableProvider'
import SearchHelper from '../../helpers/SearchHelper'
import ActionCableConsumer from '../../consumers/ActionCableConsumer'
import Sound from '../audio'
import ConversationHelper from '../../helpers/ConversationHelper'
import WidgetPredicateGroupEvaluator from '../helpers/WidgetPredicateGroupEvaluator'
import { WidgetOutboundMessage } from '../../types'

const MessengerFrame = styled.div<{ open?: boolean, maximized?: boolean }>`
  z-index: 2147483000;
  position: fixed;
  bottom: 84px;
  right: 20px;
  transform-origin: right bottom;
  height: min(704px, 100% - 104px);
  min-height: 80px;
  width: 400px;
  max-height: 704px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 5px 40px;
  opacity: 1;
  transition: width 200ms, max-width 200ms, height 200ms, max-height 200ms, transform 300ms cubic-bezier(0, 1.2, 1, 1), opacity 83ms ease-out;
  transform: scale(0);
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  background: white;

  ${props => props.open && css`
    pointer-events: all;
    opacity: 1;
    transform: scale(1);
  `}

  ${props => props.maximized && css`
    width: 688px;
  `}

  @media screen and (max-width: ${Style.breakpoints.MOBILE}) {
    transition: none;
    ${props => props.open && css`
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      height: 100%;
      width: 100%;
      border-radius: 0;
      max-height: initial;
    `}

    ${props => props.maximized && css`
      max-width: initial;
    `}
  }

  iframe {
    width: 100%;
    height: 100%;
    position: absolute;
    font-family: system-ui, "Open Sans", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji";
    font-size-adjust: none;
    font-size: 100%;
    font-style: normal;
    letter-spacing: normal;
    font-stretch: normal;
    font-variant: normal;
    font-weight: normal;
    text-align-last: initial;
    text-decoration: none;
    text-emphasis: none;
    text-indent: 0px;
    text-shadow: none;
    text-transform: none;
    alignment-baseline: baseline;
    animation-play-state: running;
    backface-visibility: visible;
    background-color: transparent;
    background-image: none;
    baseline-shift: baseline;
    border: 0px none transparent;
    border-radius: 0px;
    inset: auto;
    box-shadow: none;
    box-sizing: content-box;
    caption-side: top;
    clear: none;
    clip: auto;
    color: inherit;
    columns: auto;
    column-fill: balance;
    column-gap: normal;
    content: normal;
    counter-increment: none;
    counter-reset: none;
    cursor: auto;
    direction: ltr;
    display: inline;
    dominant-baseline: auto;
    empty-cells: show;
    float: none;
    hyphenate-character: auto;
    hyphens: manual;
    image-rendering: auto;
    line-height: inherit;
    list-style: outside none disc;
    margin: 0px;
    max-height: none;
    max-width: none;
    min-height: 0px;
    min-width: 0px;
    opacity: 1;
    orphans: 2;
    outline-offset: 0px;
    overflow: visible;
    padding: 0px;
    page: auto;
    break-after: auto;
    break-before: auto;
    break-inside: auto;
    perspective: none;
    perspective-origin: 50% 50%;
    pointer-events: auto;
    position: static;
    quotes: none;
    resize: none;
    size: auto;
    table-layout: auto;
    transform: none;
    transform-origin: 50% 50% 0px;
    transform-style: flat;
    unicode-bidi: normal;
    vertical-align: baseline;
    white-space: normal;
    widows: 2;
    word-break: normal;
    word-spacing: normal;
    overflow-wrap: normal;
    z-index: auto;
    text-align: start;
    -webkit-font-smoothing: antialiased;
  }
`

const NotificationFrame = styled.div<{ visible?: boolean, height?: number }>`
  z-index: 2147482999;
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  bottom: 84px;
  right: 20px;
  width: 340px;
  max-width: 340px;
  max-height: 360px;
  min-height: 70px;
  height: ${props => props.height !== undefined ? `${Math.min(props.height, 350) + 10}px` : '0px'};
  pointer-events: none;
  opacity: 0;
  transform: scale(0);
  transition: opacity 200ms ease, height 150ms ease;
  overflow-x: hidden;

  &:hover {
    min-height: 80px;
  }

  ${props => props.visible && css`
    pointer-events: all;
    opacity: 1;
    transform: scale(1);
  `}

  iframe {
    width: 100%;
    height: 100% !important;
    position: absolute;
    font-family: system-ui, "Open Sans", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji";
    font-size-adjust: none;
    font-size: 100%;
    font-style: normal;
    letter-spacing: normal;
    font-stretch: normal;
    font-variant: normal;
    font-weight: normal;
    text-align-last: initial;
    text-decoration: none;
    text-emphasis: none;
    text-indent: 0px;
    text-shadow: none;
    text-transform: none;
    alignment-baseline: baseline;
    animation-play-state: running;
    backface-visibility: visible;
    background-color: transparent;
    background-image: none;
    baseline-shift: baseline;
    border: 0px none transparent;
    border-radius: 0px;
    inset: auto;
    box-shadow: none;
    box-sizing: content-box;
    caption-side: top;
    clear: none;
    clip: auto;
    color: inherit;
    columns: auto;
    column-fill: balance;
    column-gap: normal;
    content: normal;
    counter-increment: none;
    counter-reset: none;
    cursor: auto;
    direction: ltr;
    display: inline;
    dominant-baseline: auto;
    empty-cells: show;
    float: none;
    height: auto;
    hyphenate-character: auto;
    hyphens: manual;
    image-rendering: auto;
    line-height: inherit;
    list-style: outside none disc;
    margin: 0px;
    max-height: none;
    max-width: none;
    min-height: 0px;
    min-width: 0px;
    opacity: 1;
    orphans: 2;
    outline-offset: 0px;
    overflow: visible;
    padding: 0px;
    page: auto;
    break-after: auto;
    break-before: auto;
    break-inside: auto;
    perspective: none;
    perspective-origin: 50% 50%;
    pointer-events: auto;
    position: static;
    quotes: none;
    resize: none;
    size: auto;
    table-layout: auto;
    transform: none;
    transform-origin: 50% 50% 0px;
    transform-style: flat;
    unicode-bidi: normal;
    vertical-align: baseline;
    white-space: normal;
    widows: 2;
    word-break: normal;
    word-spacing: normal;
    overflow-wrap: normal;
    z-index: auto;
    text-align: start;
    -webkit-font-smoothing: antialiased;
  }
`

type IProps = {
  settings?: BizzeySetting
} & IDispatchToProps & IStateToProps

interface IStateToProps {
  application: ApplicationState
}

interface IDispatchToProps {
  setApplicationState: typeof setApplicationState
  setBizzeySetting: typeof setBizzeySetting
  setWidget: typeof setWidget
  setWidgetSession: typeof setWidgetSession
  setWidgetCounters: typeof setWidgetCounters
  setMessengerFrame: typeof setMessengerFrame
  showLauncher: typeof showLauncher
  hideLauncher: typeof hideLauncher
  open: typeof open
  close: typeof close
  toggleOpen: typeof toggleOpen
  navigate: typeof navigate
  addShowLauncherCallback: typeof addShowLauncherCallback
  addHideLauncherCallback: typeof addHideLauncherCallback
  addReadyCallback: typeof addReadyCallback
  addOpenCallback: typeof addOpenCallback
  addCloseCallback: typeof addCloseCallback
  maximizeWindow: typeof maximizeWindow
  minimizeWindow: typeof minimizeWindow
  toggleWindowSize: typeof toggleWindowSize
  setHostWindowSize: typeof setHostWindowSize
}
interface IState {
  previewUrl: string
  actionCable: ActionCable.Cable | null
  message: ConversationMessage | null
  notificationFrame: HTMLIFrameElement | null
  notificationHeight: number
}


class AppClass extends React.Component<IProps, IState> {
  private messengerFrame = React.createRef<HTMLIFrameElement>()
  private pingTimer: NodeJS.Timer | null = null
  private outboundTimer: NodeJS.Timer | null = null
  private outboundTimeOnPage: number = 0
  private outboundMatchedIds: Set<string> = new Set()
  private originalPushState: History['pushState'] | null = null
  private originalReplaceState: History['replaceState'] | null = null
  private lastNavigateHref: string | null = null

  constructor(props: IProps) {
    super(props)

    this.state = {
      previewUrl: null,
      actionCable: null,
      message: null,
      notificationFrame: null,
      notificationHeight: 0,
    }

    this.debouncedFetchCounters = debounce(this.debouncedFetchCounters.bind(this), 500)
    this.fetchCounters = this.fetchCounters.bind(this)
    this.initialise = this.initialise.bind(this)
    this.initialisePing = this.initialisePing.bind(this)
    this.clearPing = this.clearPing.bind(this)
    this.processConversationUpsert = debounce(this.processConversationUpsert.bind(this), 250)
    this.onMessengerFrameLoad = this.onMessengerFrameLoad.bind(this)
    this.onNotificationFrameLoad = this.onNotificationFrameLoad.bind(this)
    this.onNotificationHeightChange = this.onNotificationHeightChange.bind(this)
    this.onMessageReceive = this.onMessageReceive.bind(this)
    this.onWindowResize = debounce(this.onWindowResize.bind(this), 100)
    this.onToggleOpenClick = this.onToggleOpenClick.bind(this)
    this.onPreviewModalClose = this.onPreviewModalClose.bind(this)
    this.onNotificationPreviewClick = this.onNotificationPreviewClick.bind(this)
    this.onNotificationPreviewClose = this.onNotificationPreviewClose.bind(this)
    this.onActionCableConnected = this.onActionCableConnected.bind(this)
    this.onActionCableDisconnected = this.onActionCableDisconnected.bind(this)
    this.onActionCableReceived = this.onActionCableReceived.bind(this)
    this.onHostNavigate = debounce(this.onHostNavigate.bind(this), 300)
    this.onNotificationPreviewClose = this.onNotificationPreviewClose.bind(this)

    // SDK methods
    this.showLauncher = this.showLauncher.bind(this)
    this.hideLauncher = this.hideLauncher.bind(this)
    this.open = this.open.bind(this)
    this.close = this.close.bind(this)
    this.navigate = this.navigate.bind(this)
    this.showNewConversation = this.showNewConversation.bind(this)
    this.showArticle = this.showArticle.bind(this)
    this.showNews = this.showNews.bind(this)
    this.showTour = this.showTour.bind(this)
    this.onShowLauncher = this.onShowLauncher.bind(this)
    this.onHideLauncher = this.onHideLauncher.bind(this)
    this.onOpen = this.onOpen.bind(this)
    this.onClose = this.onClose.bind(this)
  }

  debouncedFetchCounters() {
    this.fetchCounters().catch(console.error)
  }

  async fetchCounters() {
    try {
      const counters = await WidgetController.counters()
      this.props.setWidgetCounters(counters)
    } catch (ex) {
      console.error(ex)
    }
  }

  async ping() {
    try {
      await WidgetController.ping()
    } catch (ex) {
      console.error(ex)
    }
  }

  componentDidMount(): void {
    window.addEventListener('message', this.onMessageReceive);
    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('popstate', this.onHostNavigate)
    window.addEventListener('hashchange', this.onHostNavigate)

    // Guards against stacking wrappers if AppClass is ever mounted more than
    // once on the same page (e.g. a stale widget loader re-injecting the
    // script) — without this, each mount would nest another closure around
    // history.pushState/replaceState and never unwind, eventually blowing
    // the call stack on an unrelated app navigation.
    if (!(window as any).__bizzeyHistoryPatched) {
      this.originalPushState = history.pushState.bind(history)
      this.originalReplaceState = history.replaceState.bind(history)
      history.pushState = (...args) => { this.originalPushState(...args); this.onHostNavigate() }
      history.replaceState = (...args) => { this.originalReplaceState(...args); this.onHostNavigate() }
        ; (window as any).__bizzeyHistoryPatched = true
    }

    this.initialise()
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMessageReceive);
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('popstate', this.onHostNavigate)
    window.removeEventListener('hashchange', this.onHostNavigate)
    this.clearPing()
    this.stopOutboundEvaluation()
    this.state.actionCable?.disconnect()

    // Only the instance that actually installed the patch owns the flag —
    // an instance that found it already set (see componentDidMount) never
    // touched history and must not clear it out from under whichever
    // instance did, or a later mount could stack a wrapper on top of it.
    if (this.originalPushState && this.originalReplaceState) {
      history.pushState = this.originalPushState
      history.replaceState = this.originalReplaceState
        ; (window as any).__bizzeyHistoryPatched = false
    }
  }

  async initialise() {
    // Re-reads from window.bizzeySettings (rather than the settings prop captured
    // at mount time) so a reload()/update() call picks up settings changed since boot.
    const settings: BizzeySetting = (window as any).bizzeySettings || this.props.settings

    try {
      // Initialise cookies for domain
      CookieHelper.initialise(settings)

      // Set the locale based on the settings or browser language
      let locale: PublicLocale = (settings.locale || window.navigator.language.split('-')[0] || PublicLocale.EN) as PublicLocale
      if (!Object.values(PublicLocale).includes(locale as PublicLocale)) locale = PublicLocale.EN

      // Initialize translations with api_base
      initializeTranslations(settings.api_base)

      // Set the locale for i18n and dispatch the locale action
      setLocale(locale)

      // Set the bizzey config in the state
      this.props.setBizzeySetting(settings)

      // Preload common widget sounds once the API base URL is available.
      Sound.preload([Sounds.NOTIFICATION, Sounds.SUBMIT])

      // Configure the API from the config
      BaseController.configure(settings);

      // Fetch widget configuration
      const { widget, session } = await WidgetController.get()

      // Set widget configuration in the state
      this.props.setWidget(widget)
      // Set the session in the state
      this.props.setWidgetSession(session)
      // Fetch initial counters
      this.fetchCounters()
      // Start pinging the server every 5 minutes
      this.initialisePing()
      // Evaluate outbound content predicates on the client
      this.startOutboundEvaluation()
      // Resume a tour that navigated to this page mid-flight (see showTour)
      TourRunner.resumeIfPending(widget)

      // A tour-builder link (opened from the admin's "Edit on your website"
      // button) carries a one-time token in the URL. Once consumed it's
      // moved to sessionStorage (see TourBuilder.start) so builder mode
      // survives the admin navigating around their own site in this tab.
      const builderToken = new URLSearchParams(window.location.search).get('bizzey_tour_builder')
      if (builderToken) {
        TourBuilder.start(builderToken)
      } else {
        TourBuilder.resumeIfPending()
      }

      const actionCableSource = [
        settings.api_base.endsWith('/') ? settings.api_base.slice(0, -1) : settings.api_base,
        `/cable?${SearchHelper.getQueryString({ session_id: session.id })}`
      ].join('')
      const actionCable = ActionCable.createConsumer(actionCableSource)

      // Re-initialise (e.g. via reload()) must not leak the previous socket —
      // ActionCable.createConsumer opens a connection immediately and it stays
      // open until explicitly disconnected.
      this.state.actionCable?.disconnect()

      this.setState({ actionCable: actionCable })
    } catch (ex) {
      console.error(ex)
    }
  }

  initialisePing() {
    this.clearPing()
    this.pingTimer = setInterval(() => this.ping(), 5 * 60 * 1000)
  }

  clearPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }

  startOutboundEvaluation() {
    this.stopOutboundEvaluation()
    this.outboundTimeOnPage = 0
    this.outboundMatchedIds = new Set()

    this.evaluateOutboundMessage()

    this.outboundTimer = setInterval(() => {
      this.outboundTimeOnPage += 1
      this.evaluateOutboundMessage()
    }, 1000)
  }

  stopOutboundEvaluation() {
    if (this.outboundTimer) {
      clearInterval(this.outboundTimer)
      this.outboundTimer = null
    }
  }

  async evaluateOutboundMessage() {
    try {
      const outboundMessages = this.props.application.widget?.outbound_messages || []
      if (!outboundMessages?.length) return

      const { application: { locale } } = this.props
      const predicateObject = {
        current_url: window.location.href,
        time_on_page: this.outboundTimeOnPage,
        locale: locale,
      }
      const newlyMatchedIds: string[] = []

      for (const message of outboundMessages) {
        if (!message.client_predicate_group || !message.id) continue
        if (this.outboundMatchedIds.has(message.id)) continue

        if (WidgetPredicateGroupEvaluator.matches(predicateObject, message.client_predicate_group)) {
          this.outboundMatchedIds.add(message.id)
          newlyMatchedIds.push(message.id)
        }
      }

      if (newlyMatchedIds.length > 0) {
        const { application: { locale } } = this.props
        const { outbound_messages } = await OutboundMessagesController.evaluate(
          newlyMatchedIds,
          {
            current_url: window.location.href,
            time_on_page: this.outboundTimeOnPage,
            locale: locale,
          }
        )
        outbound_messages.forEach((message) => this.onOutboundMessageMatch(message))
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  onOutboundMessageMatch(message: WidgetOutboundMessage) {
    console.log('[Messenger] outbound message matched', message)
  }

  onHostNavigate() {
    // Multiple pushState/replaceState calls can fire for the same effective
    // URL (e.g. a host app syncing filter state back into the query string
    // without an actual change) — only treat it as a navigation if the URL
    // actually moved.
    if (this.lastNavigateHref === window.location.href) return
    this.lastNavigateHref = window.location.href

    console.log('[Messenger] host navigate', window.location.href)
    const { application: { widget } } = this.props
    if (!widget?.outbound_messages) return
    this.startOutboundEvaluation()
  }

  processConversationUpsert(conversation: Conversation) {
    const { application } = this.props
    const lastMessageMetadata = ConversationHelper.getLastMessageMetadata(conversation)

    if (lastMessageMetadata?.message) {
      const lastMessage = lastMessageMetadata.message
      if (conversation.state !== ConversationState.CLOSED && !conversation.user_read && lastMessage?.author?.type === ConversationMessageAuthorType.USER) {
        Sound.play(Sounds.NOTIFICATION)

        if (!application?.open) {
          this.props.showLauncher()
          this.setState({ message: lastMessage })
        }
      }
    }
  }

  showLauncher() {
    this.props.showLauncher()
  }

  hideLauncher() {
    this.props.hideLauncher()
  }

  open() {
    this.props.open()
  }

  close() {
    this.props.close()
  }

  navigate(space: WidgetSpaceType) {
    this.props.navigate(space)
  }

  showNewConversation() {
    this.open()
    MessageHelper.sendMessengerFrameMessage({
      type: WidgetClientEventType.SHOW_NEW_CONVERSATION,
    })
  }

  showConversation(conversationId: string) {
    MessageHelper.sendMessengerFrameMessage({
      type: WidgetClientEventType.SHOW_CONVERSATION,
      conversationId: conversationId,
    })

    requestAnimationFrame(() => {
      this.open()
    })
  }

  showArticle(articleId: string) {
    this.open()
    MessageHelper.sendMessengerFrameMessage({
      type: WidgetClientEventType.SHOW_ARTICLE,
      articleId: articleId
    })
  }

  showNews(newsId: string) {
    this.open()
    MessageHelper.sendMessengerFrameMessage({
      type: WidgetClientEventType.SHOW_NEWS,
      newsId: newsId
    })
  }

  // Unlike showArticle/showNews, this doesn't go through the messenger
  // iframe — driver.js needs to attach overlays to elements in the host
  // page's own DOM, which the sandboxed messenger iframe can't reach.
  showTour(tourId: string) {
    const widget = this.props.application.widget
    const tour = widget?.tours?.find(tour => tour.id === tourId)

    if (widget && tour) TourRunner.start(widget, tour)
  }

  onShowLauncher(callback: () => void) {
    this.props.addShowLauncherCallback(callback)
  }

  onHideLauncher(callback: () => void) {
    this.props.addHideLauncherCallback(callback)
  }

  onReady(callback: () => void) {
    this.props.addReadyCallback(callback)
  }

  onOpen(callback: () => void) {
    this.props.addOpenCallback(callback)
  }

  onClose(callback: () => void) {
    this.props.addCloseCallback(callback)
  }

  reload() {
    this.initialise()
  }

  onMessengerFrameLoad(messengerFrame: HTMLIFrameElement) {
    this.props.setMessengerFrame(messengerFrame)
  }

  onNotificationFrameLoad(notificationFrame: HTMLIFrameElement | null) {
    this.setState({ notificationFrame })
  }

  onNotificationHeightChange(height: number) {
    this.setState({ notificationHeight: height })
  }

  onMessageReceive(event: MessageEvent<WidgetMessengerEvent>) {
    if (!event?.data?.type) return
    switch (event.data.type) {
      case WidgetMessengerEventType.INITIALIZED:
        const { application: { onReadyCallbacks } } = this.props

        onReadyCallbacks.forEach(callback => callback())

        this.onWindowResize()
      case WidgetMessengerEventType.CLOSE:
        this.close()
        break
      case WidgetMessengerEventType.MAXIMIZE_WINDOW:
        this.props.maximizeWindow()
        break
      case WidgetMessengerEventType.MINIMIZE_WINDOW:
        this.props.minimizeWindow()
        break
      case WidgetMessengerEventType.TOGGLE_WINDOW_SIZE:
        this.props.toggleWindowSize()
        break
      case WidgetMessengerEventType.PREVIEW_ATTACHMENT:
        this.setState({ previewUrl: event.data.url })
        break
    }
  }

  onActionCableConnected() {
    console.log('[Messenger] connected')
  }

  onActionCableDisconnected() {
    console.log('[Messenger] Disconnected')
  }

  onActionCableReceived(event: WidgetSessionChannelEvent) {
    console.log('[Messenger] received event', event)

    switch (event.type) {
      case WidgetSessionChannelEventType.CONVERSATION_CREATE:
      case WidgetSessionChannelEventType.CONVERSATION_UPDATE:
        this.processConversationUpsert(event?.data?.conversation)
        this.debouncedFetchCounters()
        break
      case WidgetSessionChannelEventType.CONVERSATION_DELETE:
        this.debouncedFetchCounters()
        break
    }
  }

  onWindowResize() {
    this.props.setHostWindowSize(window.innerWidth, window.innerHeight)
  }

  onToggleOpenClick() {
    this.setState({ message: null })
    this.props.toggleOpen()
  }

  onNotificationPreviewClick(message: ConversationMessage) {
    this.setState({ message: null })
    this.showConversation(message.conversation_id)
  }

  onNotificationPreviewClose() {
    this.setState({ message: null })
    this.props.close()
  }

  onPreviewModalClose() {
    this.setState({ previewUrl: null })
  }

  getTheme() {
    const { application: { widget } } = this.props

    if (!widget) return DEFAULT_THEME

    return {
      ...DEFAULT_THEME,
      color: {
        action: widget.action_color,
        actionText: readableColor(widget.action_color),
        background: widget.background_color,
        backgroundText: readableColor(widget.background_color)
      }
    }
  }

  render() {
    const {
      application: {
        messengerFrame,
        launcherVisible,
        open,
        maximized,
        widget,
        session,
      }
    } = this.props
    const { actionCable, previewUrl, message, notificationFrame, notificationHeight } = this.state

    if (!widget || !actionCable) return null

    return (
      <ActionCableProvider cable={actionCable}>
        <ActionCableConsumer
          channel={{ channel: 'WidgetSessionChannel', id: session.id }}
          onConnected={this.onActionCableConnected}
          onDisconnected={this.onActionCableDisconnected}
          onReceived={this.onActionCableReceived}
        >
          <ThemeProvider theme={this.getTheme()}>
            <WidgetContainer>
              <MessengerFrame open={open} maximized={maximized} className='messenger-frame'>
                <iframe
                  ref={this.onMessengerFrameLoad}
                  name="widget"
                  title="Bizzey"
                  data-widget-frame="true"
                  role="dialog"
                >
                  {messengerFrame?.contentWindow.document.body && createPortal(
                    <Messenger head={messengerFrame.contentWindow.document.head} />,
                    messengerFrame.contentWindow.document.body
                  )}
                </iframe>
              </MessengerFrame>

              <NotificationFrame visible={!open && message !== null} height={notificationHeight} className='notification-frame'>
                <iframe
                  ref={this.onNotificationFrameLoad}
                  name="notification"
                  title="Notification"
                  allowTransparency={true}
                >
                  {notificationFrame?.contentWindow.document.body && createPortal(
                    <Notification
                      head={notificationFrame.contentWindow.document.head}
                      message={!open ? message : null}
                      onClick={this.onNotificationPreviewClick}
                      onClose={this.onNotificationPreviewClose}
                      onHeightChange={this.onNotificationHeightChange}
                    />,
                    notificationFrame.contentWindow.document.body
                  )}
                </iframe>
              </NotificationFrame>

              {launcherVisible && <Launcher open={open} onClick={this.onToggleOpenClick} />}
            </WidgetContainer>
            <RouteChangeListener />

            <PreviewModal
              url={previewUrl}
              onClose={this.onPreviewModalClose}
            />
          </ThemeProvider>
        </ActionCableConsumer>
      </ActionCableProvider>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const { application } = state

  return {
    application: application
  }
}

const mapDispatchToProps: IDispatchToProps = {
  setApplicationState,
  setBizzeySetting,
  setWidget,
  setWidgetSession,
  setWidgetCounters,
  setMessengerFrame,
  showLauncher,
  hideLauncher,
  open,
  close,
  toggleOpen,
  navigate,
  addShowLauncherCallback,
  addHideLauncherCallback,
  addReadyCallback,
  addOpenCallback,
  addCloseCallback,
  maximizeWindow,
  minimizeWindow,
  toggleWindowSize,
  setHostWindowSize,
}

export { AppClass }

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(AppClass)
